import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cartao, Indicador, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { data, dinheiro, nomePessoa } from '../../nucleo/formato';
import { COR_CONTRATO, CONTRATOS, type Contratos as DadosContratos } from './tipos';

function Quando({ dias }: { dias: number }) {
  const { t } = useTranslation('pessoas');
  const cor = dias < 0 ? 'perigo' : dias <= 15 ? 'laranja' : 'neutro';
  return <Selo cor={cor}>{dias < 0 ? t('contratos.vencidoHa', { n: Math.abs(dias) }) : dias === 0 ? t('contratos.hoje') : t('contratos.emDias', { n: dias })}</Selo>;
}

function Lista({ vazio, children }: { vazio: string; children: ReactNode[] }) {
  return children.length === 0 ? <p className="text-sm text-tinta-3">{vazio}</p> : <ul className="divide-y divide-borda">{children}</ul>;
}

function Pessoa({ id, nome, detalhe, fim }: { id: string; nome: string; detalhe: string; fim: ReactNode }) {
  return (
    <li className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-sm">
      <Link to={`/pessoas/pessoas/funcionario/${id}`} className="flex min-w-0 flex-col hover:text-acento-forte">
        <span className="truncate font-medium" title={nomePessoa(nome)}>{nomePessoa(nome)}</span>
        <span className="truncate text-xs text-tinta-3" title={detalhe}>{detalhe}</span>
      </Link>
      {fim}
    </li>
  );
}

/** Visão por tipo de contrato, admissões recentes e vencimentos de experiência (45 e 90 dias). */
export function Contratos() {
  const { t } = useTranslation('pessoas');
  const q = useQuery({ queryKey: ['pessoas', 'contratos'], queryFn: () => get<DadosContratos>('/pessoas/contratos') });

  return (
    <Estado consulta={q}>
      {(d) => {
        const total = d.por_tipo.reduce((a, x) => a + x.total, 0);
        return (
          <div className="flex min-w-0 flex-col gap-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Indicador rotulo={t('contratos.total')} valor={total} />
              {CONTRATOS.map((c) => {
                const linha = d.por_tipo.find((x) => x.contrato_tipo === c);
                return <Indicador key={c} rotulo={t(`contrato.${c}`)} valor={linha?.total ?? 0} detalhe={linha?.folha_centavos ? t('contratos.folha', { valor: dinheiro(linha.folha_centavos) }) : undefined} />;
              })}
            </div>
            {d.por_tipo.length === 0 && <p className="text-sm text-tinta-3">{t('vazio.contratos')}</p>}

            <div className="grid min-w-0 gap-4 lg:grid-cols-2">
              <Cartao titulo={t('contratos.experiencia')}>
                <p className="mb-2 text-xs text-tinta-3">{t('contratos.experienciaAjuda')}</p>
                <Lista vazio={t('contratos.semExperiencia')}>
                  {d.experiencia.map((e) => (
                    <Pessoa
                      key={e.id}
                      id={e.id}
                      nome={e.nome}
                      detalhe={`${t('contratos.marco', { dias: e.proximo_marco, data: data(e.proximo_marco === 45 ? e.fim_45 : e.fim_90) })}${e.funcao_nome ? ` / ${e.funcao_nome}` : ''}`}
                      fim={<Quando dias={e.dias_para_marco} />}
                    />
                  ))}
                </Lista>
              </Cartao>

              <Cartao titulo={t('contratos.fimPrevisto')}>
                <Lista vazio={t('contratos.semFim')}>
                  {d.fim_previsto.map((e) => (
                    <Pessoa
                      key={e.id}
                      id={e.id}
                      nome={e.nome}
                      detalhe={`${t(`contrato.${e.contrato_tipo}`)} / ${data(e.fim_contrato)}`}
                      fim={<Quando dias={e.dias_para_fim} />}
                    />
                  ))}
                </Lista>
              </Cartao>

              <Cartao titulo={t('contratos.admissoes')} className="lg:col-span-2">
                <Lista vazio={t('contratos.semAdmissoes')}>
                  {d.admissoes.map((e) => (
                    <Pessoa
                      key={e.id}
                      id={e.id}
                      nome={e.nome}
                      detalhe={`${data(e.admissao)}${e.funcao_nome ? ` / ${e.funcao_nome}` : ''}`}
                      fim={<Selo cor={COR_CONTRATO[e.contrato_tipo]}>{t(`contrato.${e.contrato_tipo}`)}</Selo>}
                    />
                  ))}
                </Lista>
              </Cartao>
            </div>
          </div>
        );
      }}
    </Estado>
  );
}
